import { Button } from '@factorialco/deprecated-design-system'
import {
  PurchaseRequestDetailDocument,
  RejectPurchaseRequestDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  id: number
  onClose: () => void
  handleUpdateApprovalStatus: (isApprovalStatusUpdated: boolean) => void
}

const RejectionModal = ({ id, onClose, handleUpdateApprovalStatus }: Props) => {
  const { company } = useCurrentSession()

  const [rejectPurchaseRequest, { loading }] = useMutation(RejectPurchaseRequestDocument, {
    extractorFn: (data) => data.procurementMutations.rejectProcurementPurchaseRequest,
    refetchQueries: [PurchaseRequestDetailDocument],
  })

  const form = useForm({
    defaultValues: { id: id, companyId: company.id, reason: '' },
    onSubmit: async (data) => {
      const result = await rejectPurchaseRequest({
        variables: { id: id, companyId: company.id, rejectionReason: data.reason },
        refetchQueries: [PurchaseRequestDetailDocument],
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('procurement.purchase_requests.detail.responses.reject.error'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('procurement.purchase_requests.detail.responses.reject.success'),
        })

        track(trackingEvents.procurement.rejectPurchaseRequest, {
          reason: data.reason,
          from: 'sidepanel',
        })

        handleUpdateApprovalStatus(true)
        handleClose()
      }

      return handled
    },
  })

  const handleReject = () => {
    form.handleSubmit()
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('procurement.purchase_requests.detail.rejection_modal.title')}
      description={i18n.t('procurement.purchase_requests.detail.rejection_modal.description')}
      footer={
        <Button onClick={handleReject} disabled={loading}>
          {i18n.t('procurement.purchase_requests.detail.actions.reject')}
        </Button>
      }
      onClose={onClose}
    >
      <TextArea
        placeholder={i18n.t('procurement.purchase_requests.detail.rejection_modal.placeholder')}
        value={form.values.reason}
        onChange={(reason) => form.setFieldValue('reason', reason)}
      />
    </Modal>
  )
}

export default RejectionModal
